/**
 * Sortable table of every CBO in the legacy_trust data. Only rendered for
 * the internal Trust Marketplace CBO. Clicking a row sets the drill-down
 * context so all other cards re-fetch with that CBO's data.
 */

import React, { useMemo, useState } from 'react';
import { Search, ArrowUpDown, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useCBOList, type CBORow } from '@/hooks/ml';
import { useDrillDown } from './DrillDownContext';

type SortKey = keyof CBORow;

function safeNum(v: unknown): number {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  return 0;
}

export const CBOComparisonCard: React.FC = () => {
  const { data, isLoading, isError, error } = useCBOList();
  const { setDrill, drillCboId } = useDrillDown();
  const [query, setQuery] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('engagements_90d');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const sorted = useMemo<CBORow[]>(() => {
    const rows = (data?.cbos ?? []) as CBORow[];
    const q = query.trim().toLowerCase();
    const filtered = q
      ? rows.filter(
          (r) =>
            String(r.cbo_id).includes(q) ||
            (r.cbo_name ?? '').toLowerCase().includes(q),
        )
      : rows;
    const sgn = sortDir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      const an = typeof av === 'number' ? av : 0;
      const bn = typeof bv === 'number' ? bv : 0;
      if (typeof av === 'string' || typeof bv === 'string') {
        return sgn * String(av ?? '').localeCompare(String(bv ?? ''));
      }
      return sgn * (an - bn);
    });
  }, [data, query, sortKey, sortDir]);

  const toggleSort = (k: SortKey) => {
    if (k === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(k);
      setSortDir('desc');
    }
  };

  // 403 means caller isn't internal — hide the card entirely.
  const hidden =
    isError && error instanceof Error && /403|internal_only|cross_cbo/.test(error.message);
  if (hidden) return null;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">Compare CBOs</CardTitle>
        <div className="relative">
          <Search className="absolute left-2 top-2 h-3.5 w-3.5 text-gray-400" />
          <input
            type="text"
            placeholder="Filter by name or ID…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="rounded-md border border-gray-200 bg-white pl-7 pr-2 py-1 text-xs w-56"
          />
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-sm text-gray-500 py-4">Loading…</div>
        ) : !sorted.length ? (
          <div className="text-sm text-gray-500 py-4">
            {query ? 'No CBOs match your filter.' : 'No CBOs with legacy data.'}
          </div>
        ) : (
          <div className="overflow-x-auto max-h-[28rem]">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
                  <SortableTh active={sortKey === 'cbo_id'} dir={sortDir} onClick={() => toggleSort('cbo_id')}>
                    ID
                  </SortableTh>
                  <SortableTh active={sortKey === 'cbo_name'} dir={sortDir} onClick={() => toggleSort('cbo_name')}>
                    CBO
                  </SortableTh>
                  <SortableTh align="right" active={sortKey === 'members_count'} dir={sortDir} onClick={() => toggleSort('members_count')}>
                    Members
                  </SortableTh>
                  <SortableTh align="right" active={sortKey === 'engagements_90d'} dir={sortDir} onClick={() => toggleSort('engagements_90d')}>
                    Engagements (90d)
                  </SortableTh>
                  <SortableTh align="right" active={sortKey === 'recipients_90d'} dir={sortDir} onClick={() => toggleSort('recipients_90d')}>
                    Recipients (90d)
                  </SortableTh>
                  <SortableTh align="right" active={sortKey === 'reply_rate_90d_pct'} dir={sortDir} onClick={() => toggleSort('reply_rate_90d_pct')}>
                    Reply % (90d)
                  </SortableTh>
                  <th className="text-right py-2 pr-1">&nbsp;</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((r) => {
                  const active = r.cbo_id === drillCboId;
                  return (
                    <tr
                      key={r.cbo_id}
                      className={`border-b border-gray-100 last:border-0 ${active ? 'bg-amber-50' : ''}`}
                    >
                      <td className="py-1.5 pr-3 tabular-nums text-gray-600">{r.cbo_id}</td>
                      <td className="py-1.5 pr-3 font-medium text-gray-900">{r.cbo_name || '—'}</td>
                      <td className="py-1.5 pr-3 text-right tabular-nums">{safeNum(r.members_count).toLocaleString()}</td>
                      <td className="py-1.5 pr-3 text-right tabular-nums">{safeNum(r.engagements_90d).toLocaleString()}</td>
                      <td className="py-1.5 pr-3 text-right tabular-nums">{safeNum(r.recipients_90d).toLocaleString()}</td>
                      <td className="py-1.5 pr-3 text-right tabular-nums">
                        {r.reply_rate_90d_pct != null ? `${r.reply_rate_90d_pct.toFixed(1)}%` : '—'}
                      </td>
                      <td className="py-1.5 pr-1 text-right">
                        <button
                          type="button"
                          onClick={() => setDrill(r.cbo_id, r.cbo_name)}
                          className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2 py-0.5 text-xs text-gray-700 hover:bg-gray-50"
                        >
                          Drill in <ChevronRight className="h-3 w-3" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const SortableTh: React.FC<{
  active: boolean;
  dir: 'asc' | 'desc';
  align?: 'left' | 'right';
  onClick: () => void;
  children: React.ReactNode;
}> = ({ active, dir, align = 'left', onClick, children }) => (
  <th className={`py-2 pr-3 ${align === 'right' ? 'text-right' : 'text-left'}`}>
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1 ${active ? 'text-indigo-700 font-semibold' : ''}`}
    >
      {children}
      <ArrowUpDown className={`h-3 w-3 ${active && dir === 'asc' ? 'rotate-180' : ''} transition-transform`} />
    </button>
  </th>
);
